import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

// Reconstruct text and a small set of formatting styles. Never render stored HTML.
export const contentTokens = (value) => {
  const doc = new DOMParser().parseFromString(String(value ?? ''), 'text/html');
  const tokens = [];
  const walk = (node, formats = []) => {
    if (node.nodeType === 3) {
      (node.textContent.match(/\s+|[^\s]+/gu) || []).forEach(text => tokens.push({ text, formats }));
      return;
    }
    const tag = node.nodeName.toLowerCase();
    if (['script', 'style', 'iframe', 'object', 'svg', 'template'].includes(tag)) return;
    const format = { strong: 'bold', b: 'bold', em: 'italic', i: 'italic', u: 'underline', s: 'strikethrough', del: 'strikethrough', code: 'code' }[tag];
    const next = format ? [...new Set([...formats, format])].sort() : formats;
    if (tag === 'br') tokens.push({ text: '\n', formats: [] });
    if (tag === 'img') tokens.push({ text: '[Image: ' + (node.getAttribute('alt') || node.getAttribute('src') || '') + ']', formats: [] });
    node.childNodes.forEach(child => walk(child, next));
    if (['p', 'div', 'li', 'blockquote', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) tokens.push({ text: '\n', formats: [] });
  };
  doc.body.childNodes.forEach(node => walk(node));
  while (tokens.length && !tokens[tokens.length - 1].text.trim()) tokens.pop();
  return tokens;
};

const same = (a, b) => a.text === b.text && a.formats.join() === b.formats.join();
export const compareContent = (before, after) => {
  const left = contentTokens(before), right = contentTokens(after);
  let start = 0, end = 0;
  while (start < left.length && start < right.length && same(left[start], right[start])) start++;
  while (end < left.length - start && end < right.length - start && same(left[left.length - 1 - end], right[right.length - 1 - end])) end++;
  const a = left.slice(start, left.length - end), b = right.slice(start, right.length - end);
  a.forEach(t => { t.changed = true; });
  b.forEach(t => { t.changed = true; });
  // Bound memory for unusually large rewrites; common prefix/suffix still match.
  if (a.length * b.length <= 1000000) {
    const rows = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
    for (let i = a.length - 1; i >= 0; i--) for (let j = b.length - 1; j >= 0; j--)
      rows[i][j] = same(a[i], b[j]) ? rows[i + 1][j + 1] + 1 : Math.max(rows[i + 1][j], rows[i][j + 1]);
    let i = 0, j = 0;
    while (i < a.length && j < b.length) {
      if (same(a[i], b[j])) { a[i++].changed = false; b[j++].changed = false; }
      else if (rows[i + 1][j] >= rows[i][j + 1]) i++;
      else j++;
    }
  }
  const textSame = left.map(t => t.text).join('') === right.map(t => t.text).join('');
  const notes = [];
  if (textSame) {
    left.forEach((t, i) => {
      if (!t.changed || !right[i]) return;
      t.formats.filter(f => !right[i].formats.includes(f)).forEach(f => notes.push('Removed ' + f + ' from “' + t.text + '”'));
      right[i].formats.filter(f => !t.formats.includes(f)).forEach(f => notes.push('Added ' + f + ' to “' + t.text + '”'));
    });
  }
  return { left, right, notes: [...new Set(notes)], markupOnly: textSame && !notes.length && before !== after };
};

const visibleTokens = (tokens, full) => {
  if (full) return tokens;
  const indexes = tokens.flatMap((t, i) => t.changed ? [i] : []);
  if (!indexes.length) return tokens.slice(0, 30);
  const visible = new Set();
  indexes.forEach(i => { for (let j = Math.max(0, i - 12); j <= Math.min(tokens.length - 1, i + 12); j++) visible.add(j); });
  const result = [];
  tokens.forEach((t, i) => {
    if (visible.has(i)) result.push(t);
    else if (!i || visible.has(i - 1)) result.push({ text: ' … ', formats: [] });
  });
  return result;
};

const DiffText = ({ tokens, full, removed }) => {
  const runs = [];
  visibleTokens(tokens, full).forEach(t => {
    const last = runs[runs.length - 1];
    if (last && last.changed === t.changed && last.formats.join() === t.formats.join()) last.text += t.text;
    else runs.push({ ...t });
  });
  return <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.7 }}>
    {runs.length ? runs.map((t, i) => <Box component="span" key={i} sx={{
      fontWeight: t.formats.includes('bold') ? 700 : 'inherit',
      fontStyle: t.formats.includes('italic') ? 'italic' : 'normal',
      fontFamily: t.formats.includes('code') ? 'monospace' : 'inherit',
      textDecoration: t.formats.includes('underline') ? 'underline' : t.formats.includes('strikethrough') ? 'line-through' : 'none',
      ...(t.changed ? { backgroundColor: removed ? '#fde2e2' : '#d9f2df', color: removed ? '#842020' : '#175d2c', borderBottom: '2px solid', borderColor: removed ? '#bc3939' : '#298346' } : {}),
    }}>{t.text}</Box>) : <Typography color="text.secondary">Empty</Typography>}
  </Box>;
};

export const RevisionDiff = ({ before, after }) => {
  const [full, setFull] = useState(false);
  const diff = useMemo(() => compareContent(before, after), [before, after]);
  return <Box>
    {diff.notes.map(note => <Typography key={note} variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{note}</Typography>)}
    {diff.markupOnly && <Typography variant="body2" sx={{ mb: 1 }}>HTML structure or attributes changed; visible text is unchanged.</Typography>}
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="caption" color="error.dark">Before · removed / changed</Typography><DiffText tokens={diff.left} full={full} removed /></Box>
      <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="caption" color="success.dark">After · added / changed</Typography><DiffText tokens={diff.right} full={full} /></Box>
    </Stack>
    <Button type="button" size="small" onClick={() => setFull(!full)}>{full ? 'Show changes only' : 'Show full content'}</Button>
    {diff.markupOnly && <Box component="details" sx={{ mt: 1 }}><summary>View HTML changes</summary><Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>Before: {before}{'\n\n'}After: {after}</Box></Box>}
  </Box>;
};
